// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, ArrowRight, Cpu, Brain, Eye, Factory, Rocket, Users, Laptop, Award, Clock, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const { currentUser, signInWithGoogleCredential } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/courses`)
      .then(res => setFeaturedCourses(res.data.slice(0, 3)))
      .catch(err => console.error('Failed to load featured courses:', err));
  }, []);

  // Google One Tap — only for signed-out visitors
  useEffect(() => {
    if (currentUser) return;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            await signInWithGoogleCredential(credential);
            navigate('/dashboard');
          } catch (err) {
            console.error('One Tap sign-in failed:', err);
          }
        },
        auto_select: true,
        cancel_on_tap_outside: false,
      });
      window.google?.accounts.id.prompt();
    };

    return () => {
      window.google?.accounts.id.cancel();
      script.remove();
    };
  }, [currentUser, signInWithGoogleCredential, navigate]);

  const features = [
    {
      icon: Rocket,
      title: "Cutting-Edge Content",
      description: "Our courses are constantly updated with the latest industry trends and technologies."
    },
    {
      icon: Users,
      title: "Expert Instructors",
      description: "Learn from industry professionals with years of real-world experience."
    },
    {
      icon: Laptop,
      title: "Learn Anywhere",
      description: "Access your courses on any device, online or offline."
    },
    {
      icon: Award,
      title: "Career Certificates",
      description: "Earn recognized certificates to boost your career prospects."
    }
  ];

  // Floating Card Component with CSS Animation
  const FloatingCard = ({ icon: Icon, title, description, style, delay = 0 }) => {
    return (
      <div
        className="glass-card floating-card"
        style={{
          padding: '20px',
          position: 'absolute',
          animationDelay: `${delay}s`,
          ...style
        }}
      >
        <div className="card-icon" style={{
          width: '50px',
          height: '50px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '15px',
          background: 'rgba(26, 82, 118, 0.1)',
          color: 'var(--primary)'
        }}>
          <Icon size={24} />
        </div>
        <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>{title}</h3>
        <p style={{ fontSize: '12px', color: 'var(--text-light)' }}>{description}</p>
      </div>
    );
  };

  // Course Card with Hover Effects
  const CourseCard = ({ course, index }) => {
    const courseId = course._id || course.id;
    return (
      <Link
        to={`/course/${courseId}`}
        className="glass-card course-card"
        style={{
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          animationDelay: `${index * 0.2}s`,
          textDecoration: 'none',
          color: 'inherit',
          display: 'block'
        }}
      >
        <div style={{
          height: '180px',
          background: course.image
            ? `url(${course.image}) center/cover no-repeat`
            : 'linear-gradient(135deg, var(--primary-light) 0%, var(--accent) 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {!course.image && (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <Cpu size={48} />
            </div>
          )}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)'
          }}></div>
        </div>
        <div style={{ padding: '20px' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 10px',
            background: 'rgba(26, 82, 118, 0.08)',
            color: 'var(--primary)',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            marginBottom: '10px'
          }}>
            {course.category}
          </span>
          <h3 style={{ fontSize: '18px', marginBottom: '10px', lineHeight: '1.4' }}>{course.title}</h3>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: 'var(--text-light)',
            fontSize: '14px',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Clock size={14} />
              <span>{course.duration}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <User size={14} />
              <span>{course.level}</span>
            </div>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '15px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: 'var(--primary)'
              }}></div>
              <span style={{ fontSize: '14px' }}>{course.instructor}</span>
            </div>
            <div style={{
              fontWeight: '700',
              color: course.price === 'FREE' ? 'var(--accent)' : 'var(--primary)'
            }}>
              {course.price ? `Ghc ${course.price}` : 'Free'}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  // Feature Card with Animation
  const FeatureCard = ({ feature, index }) => {
    return (
      <div
        className="glass-card feature-card"
        style={{
          textAlign: 'center',
          padding: '30px 20px',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
        }}
      >
        <div style={{
          width: '70px',
          height: '70px',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          background: 'rgba(26, 82, 118, 0.08)',
          color: 'var(--primary)',
          fontSize: '28px'
        }}>
          <feature.icon size={28} />
        </div>
        <h3 style={{ fontSize: '20px', marginBottom: '15px' }}>{feature.title}</h3>
        <p style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>{feature.description}</p>
      </div>
    );
  };

  return (
    <div>
      {/* Add CSS for animations */}


      {/* Hero Section */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '50px' }}>
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: '48px',
                lineHeight: '1.2',
                marginBottom: '20px',
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Empowering Africa's Future with AI & Robotics
              </h1>
              <p style={{
                fontSize: '18px',
                color: 'var(--text-light)',
                marginBottom: '30px',
                lineHeight: '1.6'
              }}>
                Join Coral Reef Innovation Africa's premier training platform to master cutting-edge technologies.
                Develop skills in artificial intelligence, robotics, and automation tailored for African challenges and opportunities.
              </p>
              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                <Link to="/courses" className="btn btn-primary">
                  <Play size={18} />
                  Start Learning
                </Link>
              </div>
            </div>

            <div style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              minHeight: '400px'
            }}>
              <div style={{
                position: 'relative',
                width: '100%',
                height: '400px'
              }}>
                <FloatingCard
                  icon={Cpu}
                  title="AI for Academic Research and Writing"
                  description="Conduct Academic Research with custom AI workflows and prompting"
                  style={{
                    width: '300px',
                    height: '200px',
                    top: 0,
                    left: 0,
                    '--rotation': '15deg'
                  }}
                  delay={0}
                />
                <FloatingCard
                  icon={Brain}
                  title="Intel AI for Youth"
                  description="Create intelligent algorithms and models"
                  style={{
                    width: '280px',
                    height: '180px',
                    top: '50px',
                    right: 0,
                    '--rotation': '3deg'
                  }}
                  delay={2}
                />
                <FloatingCard
                  icon={Eye}
                  title="Computer Vision"
                  description="Teach machines to see and interpret"
                  style={{
                    width: '250px',
                    height: '160px',
                    bottom: 0,
                    left: '30%',
                    '--rotation': '2deg'
                  }}
                  delay={4}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{ padding: '0 0 60px' }}>
        <div className="container">
          <div className="glass-card" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[
                { value: '5+', label: 'Expert-led Courses' },
                { value: '200+', label: 'Learners Enrolled' },
                { value: '3', label: 'Industry Instructors' },
              ].map((stat, i) => (
                <div key={i} style={{
                  padding: '28px 20px',
                  textAlign: 'center',
                  borderRight: i < 2 ? '1px solid var(--border)' : 'none'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--primary)', lineHeight: 1 }}>{stat.value}</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-light)', marginTop: '6px', fontWeight: '500' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '40px'
          }}>
            <h2 style={{ fontSize: '32px', color: 'var(--text)' }}>Featured Courses</h2>
            <Link to="/courses" style={{
              color: 'var(--primary)',
              textDecoration: 'none',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              View All
              <ArrowRight size={16} />
            </Link>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {featuredCourses.map((course, index) => (
              <CourseCard
                key={course._id || course.id}
                course={course}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="glass-card" style={{
            padding: '60px',
            margin: '40px 0',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
              pointerEvents: 'none'
            }}></div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '40px',
              position: 'relative',
              zIndex: 1
            }}>
              <h2 style={{ fontSize: '32px', color: 'var(--text)' }}>Why Learn With Us?</h2>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '30px',
              position: 'relative',
              zIndex: 1
            }}>
              {features.map((feature, index) => (
                <FeatureCard
                  key={index}
                  feature={feature}
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Bottom CTA */}
      <section style={{ padding: '0 0 100px' }}>
        <div className="container">
          <div style={{
            background: 'var(--primary)',
            borderRadius: '16px',
            padding: '64px 40px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse at 70% 50%, rgba(46,134,193,0.35) 0%, transparent 60%)',
              pointerEvents: 'none'
            }} />
            <h2 style={{ fontSize: '32px', color: 'white', marginBottom: '12px', fontWeight: '800', position: 'relative' }}>
              Ready to start learning?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '17px', marginBottom: '36px', maxWidth: '460px', margin: '0 auto 36px', lineHeight: '1.6', position: 'relative' }}>
              Join learners across Ghana mastering AI and robotics for Africa's future.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', position: 'relative' }}>
              <Link to="/signup" className="btn" style={{ background: 'white', color: 'var(--primary)', fontWeight: '700', padding: '12px 28px', fontSize: '15px' }}>
                Join Free
              </Link>
              <Link to="/courses" className="btn" style={{ background: 'transparent', color: 'white', border: '1.5px solid rgba(255,255,255,0.35)', padding: '12px 28px', fontSize: '15px' }}>
                Browse Courses
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;